import {
  BUDGET_ACTUAL_DEFAULT_FROM,
  BUDGET_ACTUAL_DEFAULT_TO,
  OFFER_TRACKING_START_DATE,
} from '@cortex/domain-ac-mens-hub-api';
import { useTranslation } from 'react-i18next';
import { useOfferBreakdown } from '../../hooks/useBudgetActual.js';
import { OFFER_UPLIFT_HATCH_BACKGROUND } from '../../lib/chart-theme.js';
import { formatPercent, formatYen } from '../../lib/revenue-aggregation.js';

/** 見出しのセル（クーポン利用状況の表と同じ寸法）。 @graph-connects none */
export const TH_BASE =
  'px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]';
/** 数字のセル（等幅・右寄せ）。 @graph-connects none */
export const NUM_CELL =
  'whitespace-nowrap px-3.5 py-2.5 text-right font-mono text-[12px] font-medium tabular-nums text-[var(--color-text-primary)]';

/** YYYY-MM-DD を YYYY/MM/DD にする。 @graph-connects none */
function toSlashDate(date: string): string {
  return date.replace(/-/g, '/');
}

/**
 * 表の代わりに出す一言（計測前・まだ取り込まれていない・まだ集計されていない・オファーが無い）。
 * @graph-connects none
 */
function EmptyBox({ children }: { children: string }) {
  return (
    <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[13px] text-[var(--color-text-muted)]">
      {children}
    </div>
  );
}

/**
 * 表に数えている日の範囲のうち、ページの期間と違う端。期間の頭が計測開始日より前なら開始日（from）、
 * 期間の終わりが計算済みの最終日より後なら最終日（through）。期間どおりの端は null。
 * 数え始める日（期間の頭と計測開始日の遅い方）が計算済みの最終日より後なら、期間に数えた日が無い（nothingCounted）。
 * このときは端を出さない（見出しの横に範囲を出さず、本文で「まだ集計されていない」と出す）。
 * 計測開始日は合計CPA（オファーあり）の計算と同じ共有定数で決める。
 * @graph-connects OFFER_TRACKING_START_DATE [references] オファーを数え始めた日
 */
function countedRangeOf(
  period: { from: string; to: string },
  coveredThrough: string,
): { from: string | null; through: string | null; nothingCounted: boolean } {
  const countedFrom =
    period.from < OFFER_TRACKING_START_DATE ? OFFER_TRACKING_START_DATE : period.from;
  if (countedFrom > coveredThrough) return { from: null, through: null, nothingCounted: true };
  return {
    from: period.from < OFFER_TRACKING_START_DATE ? OFFER_TRACKING_START_DATE : null,
    through: period.to > coveredThrough ? coveredThrough : null,
    nothingCounted: false,
  };
}

/**
 * 割合の横棒と数字。横棒はグラフの「オファーによる上乗せ」と同じ斜線。
 * @graph-connects formatPercent [calls] 割合の書式（予実のグラフと共通）
 */
function ShareCell({ share }: { share: number }) {
  return (
    <span className="inline-flex items-center justify-end gap-2">
      <span className="inline-block h-2 w-24 overflow-hidden rounded-full bg-[var(--color-bg-hover)]">
        <span
          data-testid="offer-share-bar"
          className="block h-full"
          style={{
            // 取り消しで割合がマイナスになる行は、負の幅が無効値として無視され全幅になるので 0 で止める
            width: formatPercent(Math.max(0, share)),
            background: OFFER_UPLIFT_HATCH_BACKGROUND,
          }}
        />
      </span>
      <span className="min-w-[3.6em] text-right">{formatPercent(share)}</span>
    </span>
  );
}

/**
 * 「オファーの内訳」と「CPAに含めない割引」が共有する、期間の埋め方・API を呼ぶかの判定・取得・見出し横の期間。
 * 両方が同じ期間・同じ問い合わせ（React Query で1回にまとまる）を使い、見出し横の期間を同じにするため1か所に置く。
 * ページの開始日・終了日の欠けた端は /targets と同じ既定期間で埋め、期間が逆順・まるごと計測開始日より前なら API を呼ばない。
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business オファーの内訳の期間を既定期間で埋め、逆順・計測前を判定して取得し、見出し横に出す期間と数えた日の範囲を求める
 * @graph-connects useOfferBreakdown [calls] オファーの内訳の取得
 * @graph-connects BUDGET_ACTUAL_DEFAULT_FROM [references] 開始日が欠けたときの既定（/targets と共有）
 * @graph-connects BUDGET_ACTUAL_DEFAULT_TO [references] 終了日が欠けたときの既定（/targets と共有）
 * @graph-connects countedRangeOf [calls] 表に数えている日の範囲
 */
export function useOfferBreakdownView(from: string | undefined, to: string | undefined) {
  const { t } = useTranslation();
  // 欠けた端は /targets と同じ既定期間で埋めてから API に渡す
  const period = { from: from ?? BUDGET_ACTUAL_DEFAULT_FROM, to: to ?? BUDGET_ACTUAL_DEFAULT_TO };
  // 期間が逆順なら API は 400 になり、期間がまるごと計測開始日より前ならオファーはそもそも数えていないので、
  // どちらも API を呼ばない。両方に当たるときは、開始日を自分で入れたなら逆順を先に知らせ、開始日を既定値で
  // 埋めたなら（終了日だけを計測開始前にした）計測前を先に知らせる
  const reversedPeriod = period.from > period.to;
  const endsBeforeTracking = period.to < OFFER_TRACKING_START_DATE;
  const reversed = reversedPeriod && (from !== undefined || !endsBeforeTracking);
  const beforeTracking = endsBeforeTracking && !reversed;
  const query = useOfferBreakdown(period.from, period.to, !beforeTracking && !reversed);
  const data = beforeTracking || reversed ? undefined : query.data;
  const coveredThrough = data?.coveredThrough ?? null;
  // 見出しの横の期間。計測前は API を呼ばないので、埋めた期間が正しい向きならそれを出す
  const shownPeriod = data ?? (beforeTracking && period.from <= period.to ? period : undefined);
  // 表に数えている日がページの期間と違うときの端（計測開始日・計算済みの最終日）
  const range =
    data !== undefined && coveredThrough !== null && !beforeTracking
      ? countedRangeOf(data, coveredThrough)
      : null;
  // 両方の端が違えば1つの範囲で、片方だけならその端だけを出す
  const rangeLabel =
    range === null
      ? null
      : range.from !== null && range.through !== null
        ? t('budgetActual.offerBreakdown.countedRange', {
            from: toSlashDate(range.from),
            to: toSlashDate(range.through),
          })
        : range.from !== null
          ? t('budgetActual.offerBreakdown.trackedFrom', { date: toSlashDate(range.from) })
          : range.through !== null
            ? t('budgetActual.offerBreakdown.coveredThrough', {
                date: toSlashDate(range.through),
              })
            : null;

  return { beforeTracking, reversed, query, data, coveredThrough, shownPeriod, range, rangeLabel };
}

/**
 * 見出しの横の期間（「YYYY/MM/DD 〜 YYYY/MM/DD の合計・税抜」と、数えた日がページの期間と違うときの範囲）。
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business オファーの表の見出しの横に、期間と数えた日の範囲を出す
 * @graph-connects none
 */
export function OfferPeriodLabel({
  shownPeriod,
  rangeLabel,
  rangeTestId,
}: {
  shownPeriod: { from: string; to: string } | undefined;
  rangeLabel: string | null;
  rangeTestId: string;
}) {
  const { t } = useTranslation();
  if (shownPeriod === undefined) return null;
  return (
    <span className="flex flex-wrap items-baseline justify-end gap-x-3 text-xs tabular-nums text-[var(--color-text-muted)]">
      <span>
        {t('budgetActual.offerBreakdown.period', {
          from: toSlashDate(shownPeriod.from),
          to: toSlashDate(shownPeriod.to),
        })}
      </span>
      {rangeLabel !== null && <span data-testid={rangeTestId}>{rangeLabel}</span>}
    </span>
  );
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 予実管理「広告・CPA」タブの「オファーの内訳」。ページの開始日〜終了日の期間のオファー（入会と同時に使われたクーポンの割引額・税抜）のうち合計CPA（オファーあり）に数えるもの（株主優待・福利厚生は API が外して返す）を、クーポン名ごとに件数・数えた月数・オファー額・割合（横棒つき）の表で出し、最後に合計行を置く（合計・割合も数える分だけ）。表の下に、合計CPA（実績・オファーあり）は広告費と獲得数がそろった日だけで計算するため、直近のまだそろっていない日のオファーは表には含まれ合計CPAには含まれないことを注記する。粒度切替には連動しない。ページの開始日・終了日の欠けた端は /targets と同じ既定期間（共有の BUDGET_ACTUAL_DEFAULT_FROM / TO）で埋めてから API に渡し、埋めた期間の終わりが計測開始日より前なら API を呼ばずに「計測前」と出す（終了日だけを計測開始前にしても、既定の開始日との逆順で API がエラーにならないように）。埋めた期間が逆順（開始日 > 終了日）なら、API を呼ばずに「開始日が終了日より後になっています」と出し（取得失敗にしない。終了日を既定値で埋めたときは既定の終了日を添える）、開始日を自分で入れたときはこれを計測前より先に知らせる。取り込み前は「まだ取り込まれていません」と出す。見出しの横には、期間が計測開始日をまたげば「YYYY/MM/DD からの集計」、期間の終わりが計算済みの最終日より後なら「YYYY/MM/DD までの集計」（両方なら1つの範囲）を出し、オファーが無い旨は期間がすべて計算済みのときだけ出す。読み込み中・取得失敗の表示を持つ。
 * @graph-connects useOfferBreakdownView [calls] 期間の埋め方・取得・見出し横の期間（CPAに含めない割引と共有）
 * @graph-connects OfferPeriodLabel [calls] 見出しの横の期間
 * @graph-connects formatYen [calls] 金額の書式（売上集計と共通）
 * @graph-connects formatPercent [calls] 割合の書式（予実のグラフと共通）
 */
export function OfferBreakdownSection({ from, to }: { from?: string; to?: string }) {
  const { t } = useTranslation();
  const { beforeTracking, reversed, query, coveredThrough, shownPeriod, range, rangeLabel } =
    useOfferBreakdownView(from, to);
  return (
    <section aria-labelledby="offer-breakdown-title">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2
          id="offer-breakdown-title"
          className="text-base font-semibold text-[var(--color-text-primary)]"
        >
          {t('budgetActual.offerBreakdown.title')}
        </h2>
        <OfferPeriodLabel
          shownPeriod={shownPeriod}
          rangeLabel={rangeLabel}
          rangeTestId="offer-counted-range"
        />
      </div>

      {beforeTracking ? (
        <EmptyBox>{t('budgetActual.offerBreakdown.beforeTracking')}</EmptyBox>
      ) : reversed ? (
        <EmptyBox>
          {to === undefined
            ? t('budgetActual.offerBreakdown.reversedPeriodDefaultTo', {
                date: toSlashDate(BUDGET_ACTUAL_DEFAULT_TO),
              })
            : t('budgetActual.offerBreakdown.reversedPeriod')}
        </EmptyBox>
      ) : query.isPending ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : query.isError ? (
        <p role="alert" className="text-sm text-[var(--color-text-danger)]">
          {t('common.fetchError')}: {query.error.message}
        </p>
      ) : coveredThrough === null ? (
        <EmptyBox>{t('budgetActual.offerBreakdown.notImported')}</EmptyBox>
      ) : query.data.rows.length === 0 ? (
        <EmptyBox>
          {range?.nothingCounted === true
            ? t('budgetActual.offerBreakdown.notYetCounted')
            : range !== null && range.through !== null
              ? t('budgetActual.offerBreakdown.emptyThrough', {
                  date: toSlashDate(range.through),
                })
              : t('budgetActual.offerBreakdown.empty')}
        </EmptyBox>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                <tr>
                  <th className={`${TH_BASE} text-left`}>
                    {t('budgetActual.offerBreakdown.couponName')}
                  </th>
                  <th className={`${TH_BASE} text-left`}>
                    {t('budgetActual.offerBreakdown.discount')}
                  </th>
                  <th className={`${TH_BASE} text-right`}>
                    {t('budgetActual.offerBreakdown.count')}
                  </th>
                  <th className={`${TH_BASE} text-right`}>
                    {t('budgetActual.offerBreakdown.months')}
                  </th>
                  <th className={`${TH_BASE} text-right`}>
                    {t('budgetActual.offerBreakdown.amount')}
                  </th>
                  <th className={`${TH_BASE} text-right`}>
                    {t('budgetActual.offerBreakdown.share')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.rows.map((row) => (
                  <tr
                    key={row.couponName}
                    className="border-b border-[var(--color-border)] last:border-b-0"
                  >
                    <td className="px-3.5 py-2.5 font-medium text-[var(--color-text-primary)]">
                      {row.couponName}
                    </td>
                    <td className="px-3.5 py-2.5 text-[12px] text-[var(--color-text-secondary)]">
                      {row.discountLabels.join('・')}
                    </td>
                    <td className={NUM_CELL}>{row.count.toLocaleString('ja-JP')}</td>
                    <td className={NUM_CELL}>
                      {t('budgetActual.offerBreakdown.monthsValue', {
                        months: row.months.join('・'),
                      })}
                    </td>
                    <td className={NUM_CELL}>{formatYen(row.offerAmount)}</td>
                    <td className={`${NUM_CELL} w-[1%]`}>
                      <ShareCell share={row.share} />
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] font-semibold">
                  <td className="px-3.5 py-2.5 text-[var(--color-text-primary)]">
                    {t('budgetActual.offerBreakdown.total')}
                  </td>
                  <td className="px-3.5 py-2.5" />
                  <td className={`${NUM_CELL} font-semibold`}>
                    {query.data.totalCount.toLocaleString('ja-JP')}
                  </td>
                  <td className={NUM_CELL} />
                  <td className={`${NUM_CELL} font-semibold`}>
                    {formatYen(query.data.totalOfferAmount)}
                  </td>
                  <td className={`${NUM_CELL} w-[1%] font-semibold`}>
                    {formatPercent(query.data.totalOfferAmount === 0 ? 0 : 1)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      {query.isSuccess && query.data.rows.length > 0 && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {t('budgetActual.offerBreakdown.cpaScopeNote')}
        </p>
      )}
    </section>
  );
}
